<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import FinestField from '@/components/FinestField.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import PropertyStepErrors from '@/components/portfolio/PropertyStepErrors.vue'
import { missingWhen } from '@/utils/field-state'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LoanData = App.Data.EstateObject.LoanData
import LoanType = App.Enums.FinancingRequest.LoanType

const props = withDefaults(
  defineProps<{
    loanTypes: LoanType[]
    errors: string[]
    existingLoans?: LoanData[]
    showMissing?: boolean
  }>(),
  { existingLoans: () => [], showMissing: false },
)

const loanType = defineModel<LoanType | null>('loanType', { required: true })
const totalFinancingAmount = defineModel<number | null>('totalFinancingAmount', { required: true })
const equityContribution = defineModel<number | null>('equityContribution', { required: true })
const interestTermYears = defineModel<number | null>('interestTermYears', { required: true })
const repaymentRate = defineModel<number | null>('repaymentRate', { required: true })

const { t, n, locale } = useI18n()

const isMissing = (value: number | string | null): boolean => props.showMissing && value === null

const loanTypeOptions = computed(() => props.loanTypes.map((type) => ({ value: type, label: t(`loanType.${type}`) })))

const hasInterestTerm = computed(() => loanType.value !== 'variable')

const selectLoanType = (): void => {
  if (!hasInterestTerm.value) {
    interestTermYears.value = null
  }
}

const euro = (value: number | null): string =>
  value === null ? '—' : n(value, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

const balanceOf = (loan: LoanData): number | null =>
  typeof loan.current_balance === 'number' ? loan.current_balance : null

const lenderOf = (loan: LoanData): string => loan.lender_name?.trim() || t('debt.unnamedLender')

const totalRemainingDebt = computed(() => props.existingLoans.reduce((sum, loan) => sum + (balanceOf(loan) ?? 0), 0))
</script>

<template>
  <div class="flex flex-col gap-4">
    <PropertyStepErrors :messages="errors" />

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-5">
      <div class="min-w-0 xl:col-span-3">
        <FinestCard :title="t('title')" icon="pi pi-wallet" class="h-full">
          <p class="text-ink-2 mb-5 text-sm">{{ t('hint') }}</p>

          <div class="grid gap-4 sm:grid-cols-2">
            <FinestField :label="t('fields.total_financing_amount')" input-id="total-financing-amount" required>
              <FinestInputNumber
                id="total-financing-amount"
                v-model="totalFinancingAmount"
                mode="currency"
                :locale="locale"
                :max-fraction-digits="0"
                :state="missingWhen(isMissing(totalFinancingAmount))"
                :aria-label="t('fields.total_financing_amount')" />
            </FinestField>

            <FinestField :label="t('fields.equity_contribution')" input-id="equity-contribution">
              <FinestInputNumber
                id="equity-contribution"
                v-model="equityContribution"
                mode="currency"
                :locale="locale"
                :max-fraction-digits="0"
                :aria-label="t('fields.equity_contribution')" />
            </FinestField>

            <FinestField :label="t('fields.loan_type')" input-id="loan-type" required>
              <FinestSelect
                id="loan-type"
                v-model="loanType"
                :options="loanTypeOptions"
                :placeholder="t('selectPlaceholder')"
                :aria-label="t('fields.loan_type')"
                :state="missingWhen(isMissing(loanType))"
                @change="selectLoanType" />
            </FinestField>

            <div class="grid gap-4" :class="hasInterestTerm ? 'sm:grid-cols-2' : ''">
              <FinestField
                v-if="hasInterestTerm"
                :label="t('fields.interest_term_years')"
                input-id="interest-term-years"
                required>
                <FinestInputNumber
                  id="interest-term-years"
                  v-model="interestTermYears"
                  :locale="locale"
                  :suffix="t('yearsSuffix')"
                  :max-fraction-digits="0"
                  :use-grouping="false"
                  :state="missingWhen(isMissing(interestTermYears))"
                  :aria-label="t('fields.interest_term_years')" />
              </FinestField>

              <FinestField :label="t('fields.repayment_rate')" input-id="repayment-rate" required>
                <FinestInputNumber
                  id="repayment-rate"
                  v-model="repaymentRate"
                  :locale="locale"
                  suffix=" %"
                  :min-fraction-digits="2"
                  :max-fraction-digits="2"
                  :state="missingWhen(isMissing(repaymentRate))"
                  :aria-label="t('fields.repayment_rate')" />
              </FinestField>
            </div>
          </div>
        </FinestCard>
      </div>

      <div class="min-w-0 xl:col-span-2">
        <FinestCard :title="t('debt.title')" icon="pi pi-chart-line" class="h-full">
          <template v-if="existingLoans.length">
            <p class="section-label section-label--sm">{{ t('debt.total') }}</p>
            <p class="text-brand-blue-1 mt-1 text-2xl font-black tracking-tight tabular-nums">
              {{ euro(totalRemainingDebt) }}
            </p>

            <div class="border-line mt-5 flex flex-col gap-3 border-t pt-5">
              <div
                v-for="(loan, index) in existingLoans"
                :key="loan.id ?? index"
                class="flex items-baseline justify-between gap-3">
                <span class="text-ink-2 truncate text-sm">{{ lenderOf(loan) }}</span>
                <span class="shrink-0 text-sm font-semibold tabular-nums">{{ euro(balanceOf(loan)) }}</span>
              </div>
            </div>
          </template>

          <p v-else class="text-ink-2 text-sm">{{ t('debt.empty') }}</p>

          <p class="text-ink-3 mt-4 text-xs">{{ t('debt.hint') }}</p>
        </FinestCard>
      </div>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "title": "Wie soll finanziert werden?",
    "hint": "Diese Konditionen gehen so an die Banken – du kannst sie später noch anpassen.",
    "fields": {
      "total_financing_amount": "Finanzierungssumme",
      "equity_contribution": "Eigenkapital",
      "loan_type": "Gewünschte Darlehensart",
      "interest_term_years": "Zinsbindung",
      "repayment_rate": "Tilgung"
    },
    "debt": {
      "title": "Bestehende Finanzierungen",
      "total": "Aktuelle Restschuld",
      "empty": "Für dieses Objekt sind keine Finanzierungen hinterlegt.",
      "hint": "Nur zur Information – die Werte stammen aus deinem Portfolio und fließen nicht in die Anfrage ein.",
      "unnamedLender": "Ohne Bank"
    },
    "loanType": {
      "annuity": "Annuitätendarlehen",
      "variable": "Variables Darlehen"
    },
    "selectPlaceholder": "Bitte wählen",
    "yearsSuffix": " Jahre"
  },
  "en": {
    "title": "How should it be financed?",
    "hint": "These terms go to the banks as entered – you can adjust them later.",
    "fields": {
      "total_financing_amount": "Financing amount",
      "equity_contribution": "Equity",
      "loan_type": "Preferred loan type",
      "interest_term_years": "Interest rate period",
      "repayment_rate": "Repayment rate"
    },
    "debt": {
      "title": "Existing financings",
      "total": "Current remaining debt",
      "empty": "No financings are recorded for this property.",
      "hint": "For information only – the figures come from your portfolio and are not part of the request.",
      "unnamedLender": "No lender"
    },
    "loanType": {
      "annuity": "Annuity loan",
      "variable": "Variable loan"
    },
    "selectPlaceholder": "Please select",
    "yearsSuffix": " years"
  }
}
</i18n>
